import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import toast from "react-hot-toast";
import Topbar from "./Topbar";
import PlayerStats from "./PlayerStats";
import MiniRatingBars from "./MiniRatingBars";
import { Flame, Search } from "lucide-react";
import Footer from "./Footer";
import PlayerPurchaseModal from "./PlayerPurchaseModal";
import Loader from "./Loader";
// import { computeRating } from "./rating";
// import { StarRating } from "./StarRating";
import SellPlayersModal from "./SellPlayersModal";
import ConfettiAnimation from "./ConfettiAnimation";

const apiBase = process.env.REACT_APP_API_URL;

export default function TransferMarketPage() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [budget, setBudget] = useState(0);
  const [purchaseModalPlayer, setPurchaseModalPlayer] = useState(null);
  // const [profile, setProfile] = useState(0);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [ownedPlayers, setOwnedPlayers] = useState([]);
  const [statsModalPlayer, setStatsModalPlayer] = useState(null);
  const [statsModalData, setStatsModalData] = useState(null);
  const [topbarReload, setTopbarReload] = useState(0);
  const [playerRatings, setPlayerRatings] = useState({});
  // const [showScoring, setShowScoring] = useState(false);
  // const [showNotEnoughTokens, setShowNotEnoughTokens] = useState(false);
  const [token, setToken] = useState("");
  const [isSelling, setIsSelling] = useState(false);
  const [isTransferLocked, setIsTransferLocked] = useState(false);
  const [transferLockThreshold, setTransferLockThreshold] = useState(null);
  const [error, setError] = useState("");
  const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);
  const ROLE_ORDER = {
    Top: 1,
    Jng: 2,
    Mid: 3,
    Bot: 4,
    Sup: 5,
  };
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedTeam, setSelectedTeam] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [isMatchupsLive, setIsMatchupsLive] = useState(null);

  const loadMarket = useCallback(async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession(); // stable singleton

      if (!session) {
        navigate("/login");
        return;
      }

      const accessToken = session.access_token;
      setToken(accessToken);

      const res = await axios.get(`${apiBase}/api/league/${leagueId}/available-players`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!res.data.success) {
        setError(res.data.error);
        return;
      }

      setBudget(res.data.budget);
      // setProfile(res.data.availableTokens.tokens);
      setAvailablePlayers(res.data.availablePlayers);
      setIsMatchupsLive(res.data.isMatchupsLive);
      setIsTransferLocked(res.data.isTransferLocked);
      setTransferLockThreshold(res.data.transferLockThreshold ?? null);
      setOwnedPlayers(res.data.ownedPlayers);
    } catch (err) {
      console.error("Failed to load transfer market:", err);
      setError("Failed to load market.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, navigate]);

  useEffect(() => {
    loadMarket();
  }, [loadMarket]);

  useEffect(() => {
    if (!availablePlayers.length || !token) return;
    const ids = availablePlayers.map((p) => p.id).join(",");
    fetch(`${apiBase}/api/players/ratings?ids=${ids}&limit=8`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then(setPlayerRatings)
      .catch(console.error);
  }, [availablePlayers, token]);

  // =============== BUY PLAYER ===============
  const handleBuy = async (player) => {
    // Frontend safety checks
    const isBudgetPlayer = transferLockThreshold && player.dynamic_price <= transferLockThreshold;

    if (isTransferLocked && !isBudgetPlayer) {
      toast.error(`Only players priced ${transferLockThreshold.toLocaleString()} or under are available during Week 1`);
      return;
    }
    if (budget < player.dynamic_price) {
      toast.error(`Not enough league budget to complete transaction`);
      return;
    }

    // if (profile < player.token_price) {
    //   setShowNotEnoughTokens(true);
    //   toast.error(`Not enough tokens`);
    //   return;
    // }

    try {
      const res = await axios.post(
        `${apiBase}/api/league/${leagueId}/buy-player`,
        {
          playerId: player.id,
          cashPrice: player.dynamic_price,
          // tokenPrice: player.token_price,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.data.success) {
        setPurchaseModalPlayer(player);
        setTopbarReload((prev) => prev + 1);
        toast.success("Player purchased", {
          style: {
            border: "2px solid gold",
            padding: "12px",
            color: "#fff",
            background: "#111827",
          },
          iconTheme: {
            primary: "#FFD700",
            secondary: "#111827",
          },
        });
      } else {
        alert(res.data.error);
      }
    } catch (err) {
      console.error("Buy failed:", err);
      alert("Error buying player.");
    }
  };

  const handleSell = async (teamPlayer) => {
    try {
      setIsSelling(true);
      const res = await axios.post(
        `${apiBase}/api/league/${leagueId}/sell-player`,
        {
          teamPlayerId: teamPlayer.id, // user_fantasy_team_players.id
          sellPrice: teamPlayer.players.dynamic_price, // or dynamic_price if you want market sell
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.data.success) {
        toast.error(res.data.error || "Failed to sell player");
        return;
      }

      toast.success("Player sold successfully");

      // Refresh market + owned players + budget
      setTopbarReload((prev) => prev + 1);
      await loadMarket();
    } catch (err) {
      console.error("Sell failed:", err);
      toast.error("Error selling player");
    } finally {
      setIsSelling(false);
    }
  };

  const truncate = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };

  const formatNum = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "0";

    if (num >= 1_000_000) {
      return truncate(num / 1_000_000, 1) + "M";
    }

    if (num >= 1_000) {
      return truncate(num / 1_000, 1) + "K";
    }

    return truncate(num, 1).toFixed(1);
  };

  const marketPlayers = availablePlayers.filter((p) => !ownedPlayers.some((op) => op.player_id === p.id));

  const topMovers = [...marketPlayers]
    .map((p) => {
      const base = p.price;
      const current = p.dynamic_price;
      const diff = current - base;
      const percent = (diff / base) * 100;

      return { ...p, percent, diff };
    })
    .sort((a, b) => Math.abs(b.percent) - Math.abs(a.percent))
    .slice(0, 6);

  const sortedMarketPlayers = [...marketPlayers].sort((a, b) => {
    const roleDiff = ROLE_ORDER[a.role] - ROLE_ORDER[b.role];
    if (roleDiff !== 0) return roleDiff;
    return b.dynamic_price - a.dynamic_price; // highest price first within each role
  });
  const roles = ["Top", "Jng", "Mid", "Bot", "Sup"];
  const teams = [...new Set(sortedMarketPlayers.map((p) => p.teams.name))];

  const filteredPlayers = sortedMarketPlayers.filter((p) => {
    const roleMatch = selectedRole === "All" || p.role === selectedRole;

    const teamMatch = selectedTeam === "All" || p.teams.name === selectedTeam;

    const query = searchQuery.trim().toLowerCase();

    const searchMatch = !query || p.name?.toLowerCase().includes(query) || p.teams.name?.toLowerCase().includes(query);

    return roleMatch && teamMatch && searchMatch;
  });

  if (loading) return <Loader loadingTitle={"Loading Transfer Market"} />;

  if (error) return <p style={{ color: "red", marginTop: "30px" }}>{error}</p>;

  return (
    <div className="page-container">
      <Topbar leagueId={leagueId} showLinks={true} showBudget={true} reload={topbarReload} />
      <div className="main">
        {/* ROLE MODAL */}

        {/* ROLE MODAL */}
        {showRoleModal && (
          <div className="market-modal-overlay" onClick={() => setShowRoleModal(false)}>
            <div className="market-modal" onClick={(e) => e.stopPropagation()}>
              <div className="market-modal-header">
                <div>
                  <span className="market-modal-eyebrow">ROLE FILTERS</span>
                  <h3>Select Role</h3>
                  <p>Choose a role to narrow your player market.</p>
                </div>

                <button className="market-modal-close" onClick={() => setShowRoleModal(false)} aria-label="Close role selector">
                  ×
                </button>
              </div>

              <div className="market-role-grid">
                {roles.map((role) => (
                  <button
                    key={role}
                    className={`market-role-option ${selectedRole === role ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedRole(role);
                      setShowRoleModal(false);
                    }}>
                    <img src={`/roles/${role}.svg`} alt="" />
                    <span>{role}</span>

                    {selectedRole === role && <span className="market-role-check">✓</span>}
                  </button>
                ))}
              </div>

              <div className="market-modal-footer">
                <button
                  className="market-reset-button"
                  onClick={() => {
                    setSelectedRole("All");
                    setShowRoleModal(false);
                  }}>
                  Reset filter
                </button>

                <button className="market-done-button" onClick={() => setShowRoleModal(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TEAM MODAL */}

        {/* TEAM MODAL */}
        {showTeamModal && (
          <div className="market-modal-overlay" onClick={() => setShowTeamModal(false)}>
            <div className="market-modal market-team-modal" onClick={(e) => e.stopPropagation()}>
              <div className="market-modal-header">
                <div>
                  <span className="market-modal-eyebrow">TEAM FILTERS</span>
                  <h3>Select Team</h3>
                  <p>Browse available players by organization.</p>
                </div>

                <button className="market-modal-close" onClick={() => setShowTeamModal(false)} aria-label="Close team selector">
                  ×
                </button>
              </div>

              <button
                className={`market-all-teams ${selectedTeam === "All" ? "selected" : ""}`}
                onClick={() => {
                  setSelectedTeam("All");
                  setShowTeamModal(false);
                }}>
                <span className="market-all-teams-icon">◎</span>

                <span className="market-all-teams-text">
                  <strong>All Teams</strong>
                  <small>Show every organization</small>
                </span>

                {selectedTeam === "All" && <span className="market-role-check">✓</span>}
              </button>

              <div className="market-team-grid">
                {teams.map((teamName) => {
                  const team = sortedMarketPlayers.find((p) => p.teams.name === teamName)?.teams;

                  const isSelected = selectedTeam === teamName;

                  return (
                    <button
                      key={teamName}
                      className={`market-team-option ${isSelected ? "selected" : ""}`}
                      onClick={() => {
                        setSelectedTeam(teamName);
                        setShowTeamModal(false);
                      }}>
                      <div className="market-team-logo">{team?.team_logo && <img src={`/teams/${team.team_logo}`} alt="" />}</div>

                      <span>{teamName}</span>

                      {isSelected && <span className="market-role-check">✓</span>}
                    </button>
                  );
                })}
              </div>

              <div className="market-modal-footer">
                <button
                  className="market-reset-button"
                  onClick={() => {
                    setSelectedTeam("All");
                    setShowTeamModal(false);
                  }}>
                  Reset filter
                </button>

                <button className="market-done-button" onClick={() => setShowTeamModal(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

        {showSellModal && !isTransferLocked && (
          <SellPlayersModal
            ownedPlayers={ownedPlayers}
            onClose={() => setShowSellModal(false)}
            onSell={(ownedPlayer) => handleSell(ownedPlayer)}
            isSelling={isSelling}
            isMatchupsLive={isMatchupsLive}
            isTransferLocked={isTransferLocked}
          />
        )}
        {purchaseModalPlayer && (
          <>
            <PlayerPurchaseModal
              player={purchaseModalPlayer}
              ownedPlayers={ownedPlayers}
              onClose={async () => {
                setPurchaseModalPlayer(null);
                await loadMarket();
              }}
            />
            <ConfettiAnimation trigger={!!purchaseModalPlayer} />
          </>
        )}
        {/* {showNotEnoughTokens && (
          <div className="modal-overlay">
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
            >
              <div className="modal-header" style={{ justifyContent: "center", flexDirection: "column", gap: "10px" }}>
                <img src="/many-tokens.png" style={{ height: "72px", objectFit: "contain", marginTop: "-50px" }} alt="League Currency" />
                <h2 style={{ margin: "0" }}>Not enough tokens?</h2>
              </div>
              <div className="modal-body" style={{ display: "flex", alignItems: "center", flexDirection: "column", gap: "20px" }}>
                <h4 style={{ textAlign: "center", color: "rgb(240, 217, 125)", marginTop: "20px" }}>
                  You don't have enough tokens to complete your player purchase. Visit the shop?
                </h4>
                <button onClick={() => navigate(`/rewards/${leagueId}`)} className="edit-btn">
                  Visit Shop
                </button>
              </div>
            </div>
          </div>
        )} */}
        <h2
          style={{
            color: "rgba(255, 255, 255, 0.6)",
            fontSize: "12px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}>
          HIGHEST MARKET MOVERS <Flame size={18} />
        </h2>
        <div className="trending-players-market">
          <div className="top-movers">
            {topMovers.map((p) => {
              const isUp = p.diff > 0;

              return (
                <div
                  className="global-box-details-market"
                  key={p.id}
                  style={{
                    border: isUp ? "1px solid #00ff90" : "1px solid #ea3943",
                    background: isUp
                      ? "linear-gradient(25deg, #00ff9066 0%, #00ff9000 100%)"
                      : "linear-gradient(25deg,rgba(234, 57, 66, 0.4) 0%,rgba(234, 57, 66, 0) 100%)",
                    boxShadow:
                      " rgba(0, 0, 0, 0.4) 0px -4px 8px -2px inset, rgba(236, 233, 233, 0.2) -1px 3px 6px -2px inset,  rgba(0, 0, 0, 0.2) 0px 30px 20px -30px",
                  }}>
                  {/* <img
                    src={`/players/${p.player_image}`}
                    alt={p.name}
                    style={{ border: "none", background: "none", height: "120px", objectFit: "contain" }}
                    className="market-mover-player-img"
                  /> */}
                  <span style={{ display: "flex", alignItems: "flex-start", flexDirection: "column" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      <div class="player-avatar">
                        <img src={`/players/${p.player_image}`} alt={p.teams.name} />
                      </div>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "2px", flexDirection: "column" }}>
                        <div style={{ display: "flex", flexDirection: "row", gap: "2px", alignItems: "center" }}>
                          <img
                            src={`/teams/${p.teams.team_logo}`}
                            alt={p.teams.name}
                            style={{ border: "none", background: "none", width: "20px", height: "20px", objectFit: "contain" }}
                          />
                          <p className="tooltip-title" style={{ margin: "0", color: "white", fontSize: "14px" }}>
                            {p.name}
                          </p>
                        </div>
                        <p style={{ margin: "0", color: "rgb(255 255 255 / 60%)", fontSize: "10px" }}>
                          {p.role} | <b>{p.teams.name}</b>
                        </p>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", flexDirection: "column", gap: "6px", marginTop: "4px" }}>
                      <div className={`change ${isUp ? "up" : "down"}`}>
                        {isUp ? "▲" : "▼"} {p.percent.toFixed(1)}%
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                        <h3 style={{ color: "white", fontSize: "14px", margin: "0" }}>${formatNum(p.dynamic_price)}</h3>
                      </div>
                    </div>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <h2
          style={{
            color: "rgba(255, 255, 255, 0.6)",
            fontSize: "12px",
            fontWeight: "600",
            marginTop: "0",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}>
          AVAILABLE PLAYERS
        </h2>
        <div style={{ display: "flex", gap: "10px", flexDirection: "column" }}>
          <div className="market-toolbar">
            <button
              className="edit-btn"
              onClick={() => {
                if (isTransferLocked) {
                  toast.error("Selling players is disabled during Week 1");
                  return;
                }

                setShowSellModal(true);
              }}
              disabled={isTransferLocked}
              style={{
                fontSize: "12px",
                opacity: isTransferLocked ? 0.45 : 1,
                cursor: isTransferLocked ? "not-allowed" : "pointer",
              }}
              title={isTransferLocked ? "Selling is disabled during Week 1" : "Sell your players"}>
              {isTransferLocked ? "Selling Locked — Week 1" : "Sell my Players"}
            </button>
            {/* Search */}
            <div className="market-search-wrapper">
              <Search size={18} className="market-search-icon" />

              <input
                type="text"
                className="market-search-input"
                placeholder="Search players, teams, or roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              {searchQuery && (
                <button type="button" className="market-search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">
                  ×
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="market-toolbar-filters">
              <div className="market-filter">
                <label>Role:</label>
                <button onClick={() => setShowRoleModal(true)} className="market-filter-button">
                  {selectedRole}
                </button>
              </div>

              <div className="market-filter">
                <label>Team:</label>
                <button onClick={() => setShowTeamModal(true)} className="market-filter-button">
                  {selectedTeam === "All" ? "All" : selectedTeam}
                </button>
              </div>
            </div>
          </div>
          {/* Available Players List */}
          <div className="available-players-container">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "end",
              }}>
              <div className="market-header">
                <div className="player-cell">PLAYER</div>
                <div className="champs-cell">CHAMPS</div>
                <div className="points-cell">AVG PTS</div>
                <div className="kda-cell">FORM</div>
                <div>PRICE</div>
                <div>CHNG%</div>
              </div>
              {filteredPlayers.map((p) => {
                const base = p.price;
                const current = p.dynamic_price;

                const diff = current - base;
                const percent = (diff / base) * 100;
                const isUp = diff > 0;

                const champs = (p.averages?.most_played_champions || "")
                  .split(",")
                  .map((c) => c.trim())
                  .filter(Boolean);

                const top3 = champs.slice(0, 3);

                // const rating = computeRating(p?.averages);

                return (
                  <div
                    key={p.id}
                    className="market-row"
                    onClick={() => {
                      setStatsModalPlayer(p.id);
                      setStatsModalData(p);
                    }}
                    style={{ opacity: p?.status_label === null ? "1" : "0.3" }}>
                    {/* Player */}
                    <div className="player-cell">
                      {p.teams?.team_logo && (
                        <img
                          src={`/teams/${p.teams.team_logo}`}
                          alt=""
                          style={{ border: "none", background: "none", width: "20px", height: "20px", objectFit: "contain" }}
                        />
                      )}
                      <img src={`/roles/${p.role}.svg`} alt={p.role} style={{ width: "18px", height: "18px" }} />
                      {/* <div class="player-avatar">
                        <img src={`/players/${p.player_image}`} alt={p.teams.name} />
                      </div> */}
                      <h3 style={{ margin: "0", color: "white", fontSize: "14px" }}>{p.name}</h3>
                    </div>

                    {/* Role */}
                    {/* <div className="role-cell">
                      <img src={`/roles/${p.role}.svg`} alt={p.role} />
                    </div> */}

                    {/* Team */}
                    {/* <div className="team-cell">
                      {p.teams?.team_logo && (
                        <img
                          src={`/teams/${p.teams.team_logo}`}
                          alt=""
                          style={{ border: "none", background: "none", width: "20px", height: "20px", objectFit: "contain" }}
                        />
                      )}
                      <h3 style={{ margin: "0", color: "white", fontSize: "14px", fontWeight: "500" }}>{p.teams.name}</h3>
                    </div> */}

                    <div style={{ display: "flex", gap: "6px" }} className="champs-cell">
                      {top3.map((champ) => (
                        <div
                          key={champ}
                          style={{
                            width: "28px",
                            height: "28px",
                            borderRadius: "4px",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}>
                          <img
                            src={`/champion/${champ}.png`}
                            alt={champ}
                            style={{
                              width: "120%",
                              height: "120%",
                              objectFit: "cover",
                              marginLeft: "-10%",
                              marginTop: "-10%",
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="points-cell">
                      <h3 style={{ margin: 0, color: "white", fontSize: "14px", fontWeight: "500" }}>{p.averages?.avg_points.toFixed(1) ?? 0}</h3>
                    </div>

                    <div className="kda-cell">
                      {/* <h3 style={{ margin: 0, color: "white", fontSize: "14px", fontWeight: "500" }}>{p.averages?.avg_kda.toFixed(1) ?? 0}</h3> */}
                      {/* <StarRating rating={rating} /> */}
                      <MiniRatingBars recentGames={playerRatings[p.id] ?? []} />
                    </div>

                    {/* Price */}
                    <div className="price-cell">
                      <h3 style={{ margin: "0", color: "white", fontSize: "14px", fontWeight: "600" }}>${formatNum(p.dynamic_price)}</h3>
                    </div>

                    {/* Change */}
                    <div className={`change ${isUp ? "up" : "down"}`}>
                      {isUp ? "▲" : "▼"} {percent.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
              {statsModalPlayer && statsModalData && (
                <PlayerStats
                  playerId={statsModalPlayer}
                  player={statsModalData}
                  owned={ownedPlayers.some((op) => op.player_id === statsModalPlayer)}
                  budget={budget}
                  leagueId={leagueId}
                  isTransferLocked={isTransferLocked}
                  transferLockThreshold={transferLockThreshold}
                  onBuy={() => handleBuy(statsModalData)}
                  onClose={() => {
                    setStatsModalPlayer(null);
                    setStatsModalData(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
